/** Formatacao e validacao que as telas usam. Regra de negocio de verdade fica no backend; isto e so ajuda de digitacao. */

export const soDigitos = (s: string) => s.replace(/\D/g, "");

/** Formata progressivamente: 529.982.247-25. */
export function formatarCpf(valor: string): string {
  const d = soDigitos(valor).slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, "$1.$2").replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1-$2");
}

/** Formata progressivamente: 12.345.678/0001-90. */
export function formatarCnpj(valor: string): string {
  const d = soDigitos(valor).slice(0, 14);
  return d.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
}

/** Celular ou fixo com DDD, progressivo: (11) 98765-4321 ou (11) 3456-7890. */
export function formatarTelefone(valor: string): string {
  const d = soDigitos(valor).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const corte = resto.length > 8 ? 5 : 4;
  return resto.length > corte ? `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}` : `(${ddd}) ${resto}`;
}

/** CEP progressivo: 01310-100. */
export function formatarCep(valor: string): string {
  const d = soDigitos(valor).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export type TipoDeChavePix = "CPF" | "CNPJ" | "EMAIL" | "TELEFONE" | "ALEATORIA";
/** O nome de cada tipo de chave na tela (o PIX da marca na area e o PIX de recebimento no painel, spec 047). */
export const ROTULO_DA_CHAVE_PIX: Record<TipoDeChavePix, string> = { CPF: "CPF", CNPJ: "CNPJ", EMAIL: "E-mail", TELEFONE: "Telefone", ALEATORIA: "Chave aleatória" };

/**
 * A chave PIX com a mascara do tipo, enquanto digita: CPF, CNPJ e telefone ganham pontuacao; e-mail perde espaco; a
 * aleatoria fica no formato 8-4-4-4-12 em minusculas. Ajuda de digitacao: quem confere a chave e o banco.
 */
export function formatarChavePix(tipo: TipoDeChavePix | string, valor: string): string {
  if (tipo === "CPF") return formatarCpf(valor);
  if (tipo === "CNPJ") return formatarCnpj(valor);
  if (tipo === "TELEFONE") return formatarTelefone(valor);
  if (tipo === "EMAIL") return valor.replace(/\s/g, "");
  if (tipo === "ALEATORIA") {
    const h = valor.toLowerCase().replace(/[^0-9a-f]/g, "").slice(0, 32);
    return [h.slice(0, 8), h.slice(8, 12), h.slice(12, 16), h.slice(16, 20), h.slice(20)].filter(Boolean).join("-");
  }
  return valor;
}

/** As 27 unidades da federacao, pra lista de UF. */
export const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"] as const;

/** "CPF ou e-mail": se so tem digito e pontuacao de CPF, formata como CPF; senao, deixa como o usuario digitou. */
export function formatarCpfOuEmail(valor: string): string {
  return /^[\d.\-\s]*$/.test(valor) ? formatarCpf(valor) : valor;
}

export function cpfValido(valor: string): boolean {
  const d = soDigitos(valor);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dig = (n: number) => { let s = 0; for (let i = 0; i < n; i++) s += Number(d[i]) * (n + 1 - i); const r = (s * 10) % 11; return r === 10 ? 0 : r; };
  return dig(9) === Number(d[9]) && dig(10) === Number(d[10]);
}

/** Dinheiro digitado como centavos: "149700" vira "1.497,00". */
export function formatarCentavos(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export const centavosDe = (texto: string) => Number(soDigitos(texto).slice(0, 11) || "0");

export const emailValido = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const DOMINIOS_DE_EMAIL = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "yahoo.com.br", "icloud.com", "live.com", "msn.com", "uol.com.br", "bol.com.br", "terra.com.br", "globo.com", "protonmail.com"];

function distanciaDeEdicao(a: string, b: string): number {
  let anterior = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const atual = [i];
    for (let j = 1; j <= b.length; j++) atual[j] = Math.min(anterior[j]! + 1, atual[j - 1]! + 1, anterior[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    anterior = atual;
  }
  return anterior[b.length]!;
}

/**
 * Erro de digitacao no dominio do e-mail ("gmai.com", "hotmial.com"): devolve o e-mail com o dominio provavel
 * ("fulano@gmail.com") ou nulo. So ajuda de digitacao: quem decide e quem digita, e dominio de empresa nunca e sugerido.
 */
export function sugerirDominioDeEmail(email: string): string | null {
  const partes = email.trim().toLowerCase().split("@");
  if (partes.length !== 2 || !partes[0] || !partes[1]) return null;
  const [local, dominio] = partes as [string, string];
  if (DOMINIOS_DE_EMAIL.includes(dominio)) return null;
  // Uma letra de diferenca vale pra qualquer dominio; duas (letras trocadas de lugar) so pros mais compridos.
  const melhor = DOMINIOS_DE_EMAIL.map((d) => ({ d, n: distanciaDeEdicao(dominio, d) })).sort((x, y) => x.n - y.n)[0]!;
  const limite = dominio.length >= 8 ? 2 : 1;
  return melhor.n >= 1 && melhor.n <= limite ? `${local}@${melhor.d}` : null;
}

const FUSO = "America/Sao_Paulo";

/** "R$ 1.497,00". */
export const formatarReais = (centavos: number) => `R$ ${formatarCentavos(centavos)}`;

/** "US$ 39,00". O produto da Global e anunciado em dolar e cobrado em real (spec 008, decisao 3). */
export const formatarDolares = (centavos: number) => `US$ ${formatarCentavos(centavos)}`;

/**
 * A frase que explica o preco de quem e anunciado em dolar: "US$ 39,00 pela cotação PTAX de 5,0441, de 14/10/2026".
 * Desde a spec 050 a cotacao e a PTAX do Banco Central e o real acompanha cada publicacao nova; a data e a da
 * publicacao, entao a frase diz de onde o numero veio e de quando, e da pra conferir no site do Banco Central.
 */
export function precoEmDolar(usdCentavos: number, cotacao: string, cotacaoEm?: string | null): string {
  const taxa = Number(cotacao).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return `${formatarDolares(usdCentavos)} pela cotação PTAX de ${taxa}${cotacaoEm ? `, de ${formatarDia(cotacaoEm)}` : ""}`;
}

/**
 * "R$ 399,12 pela cotação PTAX de 5,0521, de 15/10/2026" (Vitor, 15/10): com o dolar em destaque, a linha de baixo diz
 * quanto sai em real, que e o que se cobra, e de onde veio a conta.
 */
export function realPelaCotacao(brlCentavos: number, cotacao: string, cotacaoEm?: string | null): string {
  const taxa = Number(cotacao).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return `${formatarReais(brlCentavos)} pela cotação PTAX de ${taxa}${cotacaoEm ? `, de ${formatarDia(cotacaoEm)}` : ""}`;
}

/**
 * Dia civil "2026-09-15" vira "15/09/2026" (sem fuso: e um dia, nao um instante). Se chegar um instante ISO, converte
 * no fuso de Sao Paulo em vez de cortar a string em UTC (G07, 4.4).
 */
export function formatarDia(dia: string): string {
  if (dia.includes("T")) return formatarData(dia);
  const [a, m, d] = dia.split("-");
  return a && m && d ? `${d}/${m}/${a}` : dia;
}

/** Data de um instante, no fuso de Sao Paulo: "14/09/2026". */
export function formatarData(iso: string | Date): string {
  return new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
}

/** Instante vira "14/09/2026 às 13:45", na hora de Brasilia. */
export function formatarDataHora(iso: string | Date): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}/${p.month}/${p.year} às ${p.hour}:${p.minute}`;
}

/** Hoje em Brasilia, como dia civil "2026-09-15". */
export const hojeEmSaoPaulo = (agora = new Date()) => new Intl.DateTimeFormat("en-CA", { timeZone: FUSO }).format(agora);

/**
 * O dia civil ("2026-09-15") que o relogio de Brasilia mostra naquele instante. Nunca `iso.slice(0, 10)`: isso e o dia
 * em UTC, que das 21h a meia-noite ja e o dia seguinte (Vitor, 19/10: datas adiantando ou atrasando um dia).
 */
export const diaEmSaoPaulo = (instante: string | Date) => hojeEmSaoPaulo(new Date(instante));

/** Um instante ISO completo ("2026-10-20T01:30:00.000Z"), e nao um dia civil: esse se mostra no fuso de Brasilia. */
export const ehInstante = (texto: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(texto);

/** "agora", "há 5 min", "há 3 h", "ontem" (o dia anterior no calendario de Brasilia); mais velho que isso vira a data. */
export function tempoRelativo(iso: string | Date, agora = new Date()): string {
  const s = Math.round((agora.getTime() - new Date(iso).getTime()) / 1000);
  if (s < 0) return formatarDataHora(iso);
  if (s < 60) return "agora";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86_400) return `há ${Math.floor(s / 3600)} h`;
  if (diaEmSaoPaulo(iso) === diaEmSaoPaulo(new Date(agora.getTime() - 86_400_000))) return "ontem";
  return formatarData(iso);
}

/**
 * Dinheiro na moeda que veio (ISO 4217): "US$ 26.509,60", "R$ 1.497,00"; outra moeda leva o codigo ("EUR 10,00").
 * Negativo com o sinal na frente: "-US$ 3.490,40".
 */
export function formatarMoeda(centavos: number, moeda: string | null | undefined): string {
  const simbolo = moeda === "USD" ? "US$" : moeda === "BRL" || !moeda ? "R$" : moeda;
  return `${centavos < 0 ? "-" : ""}${simbolo} ${formatarCentavos(Math.abs(centavos))}`;
}

/** Ganho ou perda com o sinal sempre a vista: "+US$ 1.509,60", "-US$ 3.490,40", "US$ 0,00". */
export const formatarResultado = (centavos: number, moeda: string | null | undefined) => `${centavos > 0 ? "+" : ""}${formatarMoeda(centavos, moeda)}`;

/**
 * A idade de uma leitura (o saldo da corretora, spec 043, 3.5): "visto agora", "visto há 5 min", "visto há 3 h" e,
 * passado um dia, a data e a hora ("visto em 03/10 às 14:20"; de outro ano, com o ano). Saldo velho nao se disfarca de
 * atual: a tela sempre diz quando foi visto.
 */
export function idadeDaLeitura(iso: string | Date, agora = new Date()): string {
  const quando = new Date(iso);
  const s = Math.round((agora.getTime() - quando.getTime()) / 1000);
  if (s >= 0 && s < 60) return "visto agora";
  if (s >= 0 && s < 3600) return `visto há ${Math.floor(s / 60)} min`;
  if (s >= 0 && s < 86_400) return `visto há ${Math.floor(s / 3600)} h`;
  const p = Object.fromEntries(new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).formatToParts(quando).map((x) => [x.type, x.value]));
  const anoDeAgora = new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, year: "numeric" }).format(agora);
  return `visto em ${p.day}/${p.month}${p.year === anoDeAgora ? "" : `/${p.year}`} às ${p.hour}:${p.minute}`;
}

/** Tamanho de arquivo legivel: "820 KB", "2,4 MB". */
export function formatarBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** Quanto falta pra um instante: a contagem regressiva da promocao (spec 067). */
export type Contagem = { dias: number; horas: number; minutos: number; segundos: number };

/** Nulo quando o instante ja chegou (ou nao e uma data): e a hora da contagem sumir. */
export function contagemAte(fim: string | Date, agora: Date | number = Date.now()): Contagem | null {
  const ms = new Date(fim).getTime() - (typeof agora === "number" ? agora : agora.getTime());
  if (!(ms > 0)) return null;
  // Arredonda pra cima: com meio segundo faltando ainda mostra 1 s, e o zero so aparece quando acabou de verdade.
  const s = Math.ceil(ms / 1000);
  return { dias: Math.floor(s / 86_400), horas: Math.floor((s % 86_400) / 3600), minutos: Math.floor((s % 3600) / 60), segundos: s % 60 };
}

/** "2 dias, 14 horas, 3 minutos e 12 segundos": o nome do relogio pro leitor de tela. Partes zeradas ficam de fora. */
export function contagemPorExtenso(c: Contagem): string {
  const partes = [
    c.dias ? `${c.dias} ${c.dias === 1 ? "dia" : "dias"}` : null,
    c.horas ? `${c.horas} ${c.horas === 1 ? "hora" : "horas"}` : null,
    c.minutos ? `${c.minutos} ${c.minutos === 1 ? "minuto" : "minutos"}` : null,
    c.segundos ? `${c.segundos} ${c.segundos === 1 ? "segundo" : "segundos"}` : null,
  ].filter((x): x is string => Boolean(x));
  return partes.length > 1 ? `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}` : (partes[0] ?? "0 segundos");
}
